// Auth flow primitives. Drives the Cognito Hosted UI redirect, handles the
// callback exchange, and tracks the in-memory access token plus refresh-via-cookie.
//
// Two modes (see config.authMode):
//   - "backend" (games): the SPA POSTs the code to its own /api/auth/exchange,
//     which sets an httpOnly refresh cookie, and refreshes via /api/auth/refresh.
//   - "direct" (landing): the SPA exchanges the code against Cognito directly
//     and keeps the tokens in memory/sessionStorage (no refresh cookie).

import { authCallbackPath, callbackUri, config } from "./config.js";
import { codeChallengeFromVerifier, generateCodeVerifier, generateState } from "./pkce.js";
import { decodeJwtClaims } from "./jwt.js";
import type { MeResponse } from "@couplesgames/core-types";

// Session-storage keys use a fixed prefix; each game is served from its own
// origin so per-game prefixes are unnecessary.
const SS_VERIFIER = "cg.pkce.verifier";
const SS_STATE = "cg.pkce.state";
const SS_POST_LOGIN_PATH = "cg.postLoginPath";

export interface ExchangeResult {
  accessToken: string;
  expiresIn: number;
  tokenType: "Bearer";
  idToken?: string;
}

export interface LoginResult {
  accessToken: string;
  expiresAtMs: number;
  postLoginPath: string;
  /** Present only in "direct" mode, where the ID token is available client-side. */
  idToken?: string;
}

/** Starts the Hosted UI redirect with PKCE. */
export async function startLogin(postLoginPath = "/"): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await codeChallengeFromVerifier(verifier);
  const state = generateState();

  sessionStorage.setItem(SS_VERIFIER, verifier);
  sessionStorage.setItem(SS_STATE, state);
  sessionStorage.setItem(SS_POST_LOGIN_PATH, postLoginPath);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.cognitoClientId,
    redirect_uri: callbackUri(),
    scope: "openid email profile",
    identity_provider: "Google",
    code_challenge_method: "S256",
    code_challenge: challenge,
    state,
  });

  window.location.assign(`${config.cognitoHostedUi}/oauth2/authorize?${params}`);
}

/**
 * Handles the redirect back from Cognito: validates state, exchanges the
 * code, and returns the access token (plus ID token in direct mode) and the
 * path to navigate back to. Throws on any mismatch.
 */
export async function completeLogin(): Promise<LoginResult> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const error = url.searchParams.get("error");

  if (error) {
    throw new Error(`auth error: ${error}`);
  }
  if (!code || !state) {
    throw new Error("missing code or state");
  }

  const expectedState = sessionStorage.getItem(SS_STATE);
  const verifier = sessionStorage.getItem(SS_VERIFIER);
  const postLoginPath = sessionStorage.getItem(SS_POST_LOGIN_PATH) ?? "/";

  sessionStorage.removeItem(SS_STATE);
  sessionStorage.removeItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_POST_LOGIN_PATH);

  if (!verifier || !expectedState) {
    throw new Error("missing pkce verifier or state in session storage");
  }
  if (state !== expectedState) {
    throw new Error("state mismatch");
  }

  if (config.authMode === "direct") {
    const data = await directTokenExchange(code, verifier);
    return {
      accessToken: data.access_token,
      idToken: data.id_token,
      expiresAtMs: Date.now() + data.expires_in * 1000,
      postLoginPath,
    };
  }

  const res = await fetch("/api/auth/exchange", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      code,
      redirectUri: callbackUri(),
      codeVerifier: verifier,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`exchange failed: ${res.status} ${text}`);
  }

  const data = (await res.json()) as ExchangeResult;
  return {
    accessToken: data.accessToken,
    expiresAtMs: Date.now() + data.expiresIn * 1000,
    postLoginPath,
  };
}

/** Attempts a silent refresh via the httpOnly refresh cookie (backend mode only). */
export async function trySilentRefresh(): Promise<{
  accessToken: string;
  expiresAtMs: number;
} | null> {
  if (config.authMode === "direct") return null;

  const res = await fetch("/api/auth/refresh", {
    method: "POST",
    credentials: "include",
  });

  if (!res.ok) return null;
  const data = (await res.json()) as ExchangeResult;
  return {
    accessToken: data.accessToken,
    expiresAtMs: Date.now() + data.expiresIn * 1000,
  };
}

/** Clears the server-side refresh cookie (backend mode only). */
export async function logout(): Promise<void> {
  if (config.authMode === "backend") {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  }
}

/** Build the Cognito Hosted UI logout URL. The SPA can redirect to this to
 *  invalidate the IdP session before showing the login screen again. */
export function hostedUiLogoutUrl(redirectTo: string): string {
  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    logout_uri: redirectTo,
  });
  return `${config.cognitoHostedUi}/logout?${params}`;
}

/** Decodes the ID token into an account profile (direct mode only). */
export function profileFromIdToken(idToken: string): MeResponse | null {
  const claims = decodeJwtClaims(idToken);
  if (!claims) return null;
  return {
    sub: typeof claims.sub === "string" ? claims.sub : "",
    email: typeof claims.email === "string" ? claims.email : undefined,
    name: typeof claims.name === "string" ? claims.name : undefined,
    picture: typeof claims.picture === "string" ? claims.picture : undefined,
    givenName: typeof claims.given_name === "string" ? claims.given_name : undefined,
    familyName: typeof claims.family_name === "string" ? claims.family_name : undefined,
  };
}

async function directTokenExchange(
  code: string,
  codeVerifier: string,
): Promise<{ access_token: string; id_token: string; expires_in: number }> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.cognitoClientId,
    code,
    redirect_uri: callbackUri(),
    code_verifier: codeVerifier,
  });

  const res = await fetch(`${config.cognitoHostedUi}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`token exchange failed: ${res.status} ${text}`);
  }

  return (await res.json()) as {
    access_token: string;
    id_token: string;
    expires_in: number;
  };
}

export { authCallbackPath };

// --- Direct-mode session persistence ---------------------------------------

const SS_SESSION = "cg.direct.session";

export interface StoredSession {
  accessToken: string;
  expiresAtMs: number;
  idToken?: string;
}

/** Persist a direct-mode session across reloads (browser-session lifetime). */
export function saveDirectSession(session: StoredSession): void {
  sessionStorage.setItem(SS_SESSION, JSON.stringify(session));
}

/** Restore a direct-mode session, or null if none/expired. */
export function loadDirectSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SS_SESSION);
    if (!raw) return null;
    const session = JSON.parse(raw) as StoredSession;
    if (!session || !session.accessToken || session.expiresAtMs < Date.now()) {
      sessionStorage.removeItem(SS_SESSION);
      return null;
    }
    return session;
  } catch {
    sessionStorage.removeItem(SS_SESSION);
    return null;
  }
}

/** Clear a persisted direct-mode session. */
export function clearDirectSession(): void {
  sessionStorage.removeItem(SS_SESSION);
}
